/* Gemstone Mood popup.
   Trigger API: any same-site link carrying data-cj-quiz-open whose href is the quiz page, e.g.
     <a href="/pages/gemstone-mood" data-cj-quiz-open>Take the quiz</a>
   plus {% render 'cj-quiz-modal-assets' %} on that page. Nothing else on the site is intercepted.
   The quiz shown here IS the standalone page's section: it is fetched once, mounted with the shared
   engine (CJQuiz.mount, popup mode) and reused, so questions, scoring, personas and share URLs never
   fork. Without JavaScript, on modifier-clicks, or if loading fails, the link simply opens the page.
   History: opening adds one same-URL entry, so browser Back closes the popup. Steps inside the quiz
   never touch history (the quiz's own Back button steps back). No auto-open of any kind. */
(() => {
  if (window.CJQuizModal) return;
  const doc = document;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const pages = {};
  let dialog, body, quiz, opener, scrollY0 = 0, busy = false;

  const fetchPage = (href) =>
    pages[href] || (pages[href] = fetch(href, { credentials: 'same-origin' }).then((r) => {
      if (!r.ok) throw new Error('quiz page ' + r.status);
      return r.text();
    }).catch((e) => { delete pages[href]; throw e; }));

  const loadOnce = (tag, attr, url) => new Promise((resolve, reject) => {
    if (Array.from(doc.querySelectorAll(tag)).some((n) => n.getAttribute(attr) === url)) return resolve();
    const n = doc.createElement(tag);
    if (tag === 'link') n.rel = 'stylesheet'; else n.async = true;
    n.setAttribute(attr, url);
    n.onload = resolve;
    n.onerror = reject;
    doc.head.appendChild(n);
  });

  function build() {
    dialog = doc.createElement('dialog');
    dialog.className = 'cjqm';
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', 'Gemstone mood quiz');
    dialog.dataset.cjqModal = '';
    dialog.innerHTML =
      '<div class="cjqm__bar">' +
        '<span class="cjqm__label" aria-hidden="true">Gemstone mood</span>' +
        '<span class="cjqm__track" aria-hidden="true"><i></i></span>' +
        '<button type="button" class="cjqm__close" data-cjqm-close aria-label="Close the quiz">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="cjqm__body" data-cjqm-body>' +
        '<p class="cjqm__loading" role="status"><span class="cjqm__gem" aria-hidden="true"></span>Opening the quiz…</p>' +
      '</div>';
    doc.body.appendChild(dialog);
    body = dialog.querySelector('[data-cjqm-body]');

    dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); }); // Esc
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) return close(); // backdrop (the margin around the desktop surface)
      if (e.target.closest('[data-cjqm-close]')) return close();
      // A result CTA pointing at this same page + anchor: close first, then go to the anchor.
      const a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || a.target) return;
      const u = new URL(a.href, location.href);
      if (u.hash && u.origin === location.origin && u.pathname === location.pathname && u.search === location.search) {
        e.preventDefault();
        close().then(() => { location.hash = u.hash; });
      }
    });
  }

  // Lift the quiz section out of the standalone page and mount it in popup mode.
  async function load(href) {
    const src = new DOMParser().parseFromString(await fetchPage(href), 'text/html');
    const section = src.querySelector('[data-cjq]');
    if (!section) throw new Error('quiz section missing');
    const host = section.closest('.shopify-section') || section.parentElement;
    await Promise.all(Array.from(host.querySelectorAll('link[rel="stylesheet"]')).map((l) => loadOnce('link', 'href', l.getAttribute('href'))));
    if (!(window.CJQuiz && window.CJQuiz.mount)) {
      const s = host.querySelector('script[src]');
      if (!s) throw new Error('quiz script missing');
      await loadOnce('script', 'src', s.getAttribute('src'));
    }
    const el = doc.importNode(section, true);
    // The host page keeps its own H1; inside the popup the quiz title becomes the dialog's name.
    const h1 = el.querySelector('h1');
    if (h1) {
      const h2 = doc.createElement('h2');
      Array.from(h1.attributes).forEach((a) => h2.setAttribute(a.name, a.value));
      h2.innerHTML = h1.innerHTML;
      h2.tabIndex = -1;
      h1.replaceWith(h2);
      dialog.removeAttribute('aria-label');
      dialog.setAttribute('aria-labelledby', h2.id);
    }
    el.addEventListener('cjq:step', (e) => {
      const n = e.detail.step;
      dialog.style.setProperty('--cjqm-p', n >= 7 ? 1 : n / 6);
      dialog.dataset.step = n === 0 ? 'intro' : n === 7 ? 'result' : 'q';
    });
    body.replaceChildren(el);
    quiz = window.CJQuiz.mount(el, { modal: true });
  }

  const lock = (on) => doc.documentElement.classList.toggle('cjqm-lock', on);
  const animate = (frames, ms, easing, pseudo) => {
    try { return dialog.animate(frames, { duration: ms, easing, pseudoElement: pseudo }); } catch (e) { return null; }
  };

  async function open(href, trigger) {
    if (busy || (dialog && dialog.open)) return;
    busy = true;
    opener = trigger || doc.activeElement;
    scrollY0 = window.scrollY; // the quiz's scrollIntoView can move the locked page; put it back on close
    if (!dialog) build();
    lock(true);
    dialog.showModal();
    history.pushState({ cjqModal: 1 }, '');
    const ms = reduce.matches ? 160 : 420;
    animate([{ opacity: 0 }, { opacity: 1 }], ms, 'ease-out', '::backdrop');
    animate(reduce.matches
      ? [{ opacity: 0 }, { opacity: 1 }]
      : [{ opacity: 0, transform: 'translateY(28px) scale(0.985)' }, { opacity: 1, transform: 'none' }], ms, 'cubic-bezier(0.23, 1, 0.32, 1)');
    try {
      if (!quiz) await load(href);
      quiz.reset();
      body.scrollTop = 0;
      const title = body.querySelector('.cjq__title');
      (title || dialog.querySelector('[data-cjqm-close]')).focus({ preventScroll: true });
      busy = false;
    } catch (e) {
      busy = false;
      await close();
      location.href = href; // the standalone page always works
    }
  }

  // fromHistory: the browser already went back (popstate), so don't step history again.
  function close(fromHistory) {
    if (!dialog || !dialog.open || dialog.dataset.closing) return Promise.resolve();
    dialog.dataset.closing = '1';
    const ms = reduce.matches ? 120 : 240;
    animate([{ opacity: 1 }, { opacity: 0 }], ms, 'ease-in', '::backdrop');
    const a = animate(reduce.matches
      ? [{ opacity: 1 }, { opacity: 0 }]
      : [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(16px) scale(0.99)' }], ms, 'cubic-bezier(0.77, 0, 0.175, 1)');
    return (a ? a.finished.catch(() => {}) : Promise.resolve()).then(() => {
      dialog.close();
      delete dialog.dataset.closing;
      lock(false);
      window.scrollTo({ top: scrollY0, behavior: 'instant' });
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
      if (!fromHistory && history.state && history.state.cjqModal) history.back();
    });
  }

  addEventListener('popstate', () => { if (dialog && dialog.open) close(true); });
  // Back from a page the popup linked to: if this page was reloaded rather than restored, the
  // popup entry is now an ordinary history step, so drop its marker.
  addEventListener('pageshow', () => {
    if (history.state && history.state.cjqModal && !(dialog && dialog.open)) history.replaceState(null, '');
  });

  const trigger = (e) => {
    const a = e.target.closest && e.target.closest('a[data-cj-quiz-open]');
    return a && a.origin === location.origin ? a : null;
  };
  doc.addEventListener('click', (e) => {
    const a = trigger(e);
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    open(a.href, a);
  });
  // Warm the fetch on intent so the first open is quick.
  ['pointerover', 'focusin', 'touchstart'].forEach((t) => doc.addEventListener(t, (e) => {
    const a = trigger(e);
    if (a) fetchPage(a.href).catch(() => {});
  }, { passive: true }));

  window.CJQuizModal = { open, close };
})();
